/**
 * Add Account wizard — three-step shell.
 *
 *   1. firm         — pick the prop firm. If the firm supports >1 platform
 *                     the step also surfaces an inline platform mini-picker.
 *   2. program      — pick challenge type → (program) → balance. Program
 *                     auto-collapses when the firm only has one program for
 *                     the chosen type. Firms with zero seeded variants fall
 *                     back to a plain numeric "Account Size" input.
 *   3. credentials  — login + password (+ platform-conditional field). Submit
 *                     writes the row, fires validation, and closes the dialog.
 *
 * The bot's login attempt is the confirmation. The new row appears on the
 * accounts page with a `Validating` chip; it flips to `Active` or `Failed`
 * on its own.
 */

import { useCallback, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  KeyRound,
  Loader2,
  Target,
  X,
  type LucideIcon,
} from "@/components/ui/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { createAccount, validateAccount } from "@/lib/api/accounts";
import { StepFirm } from "./step-firm";
import { StepProgram } from "./step-program";
import { StepCredentials } from "./step-credentials";
import { INITIAL_WIZARD_STATE, STEPS, type StepId, type WizardState } from "./types";
import type { AccountPhase } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the wizard completes (success OR cancel) so the
   *  parent can refetch the accounts list. */
  onClose: () => void;
  marketType?: "forex_cfd" | "futures";
}

// Expo-like easing curve used across the dashboard for card entrances.
const EASE = [0.16, 1, 0.3, 1] as const;

interface StepMeta {
  title: string;
  description: string;
  icon: LucideIcon;
}

const STEP_META: Record<StepId, StepMeta> = {
  firm: {
    title: "Choose Your Prop Firm",
    description: "Pick the firm that issued your challenge or funded account.",
    icon: Building2,
  },
  program: {
    title: "Which Account?",
    description:
      "Pick the challenge type, then the program and balance you bought.",
    icon: Target,
  },
  credentials: {
    title: "Enter Your Credentials",
    description: "We log in read-only to verify your account — no trades are placed.",
    icon: KeyRound,
  },
};

const RAIL_LABELS: Record<StepId, string> = {
  firm: "Firm",
  program: "Account",
  credentials: "Login",
};

export function AddAccountWizard({
  open,
  onOpenChange,
  onClose,
  marketType,
}: Props) {
  const [state, setState] = useState<WizardState>(INITIAL_WIZARD_STATE);
  const [step, setStep] = useState<StepId>("firm");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = useCallback(() => {
    setState(INITIAL_WIZARD_STATE);
    setStep("firm");
    setSubmitError(null);
    setIsSubmitting(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onOpenChange(false);
    onClose();
  }, [onClose, onOpenChange, reset]);

  const updateState = useCallback((patch: Partial<WizardState>) => {
    setState((s) => ({ ...s, ...patch }));
  }, []);

  // Submit: write the row, kick the bot's validation pass, close. The
  // accounts page already renders a "Validating" chip on the row and
  // flips to "Active" or "Failed" on its next refetch — no blocking
  // spinner modal here.
  const submitCredentials = useCallback(async () => {
    if (isSubmitting) return;
    if (!state.platform || !state.firm) return;
    setIsSubmitting(true);
    setSubmitError(null);
    const id = `${state.firm.id}-${randomSuffix(6)}`.slice(0, 32);
    // Instant-funding programs skip evaluation entirely — the account
    // is funded from day 1. Everything else starts at step1;
    // advance-phase derives the next phase from the configured step
    // count so a 1-step program moves straight to funded.
    const phase: AccountPhase =
      state.challengeType === "instant" ? "funded" : "step1";
    try {
      await createAccount({
        id,
        firmId: state.firm.id,
        platformType: state.platform,
        adapter: "playwright",
        role: state.role,
        phase,
        login: state.login,
        password: state.password,
        brokerServer: state.server || null,
        accountLabel: state.accountLabel.trim() || null,
        loginUrl: state.loginUrl.trim() || null,
        accountSize: state.accountSize,
        steps: state.steps,
        challengeType: state.challengeType,
        programName: state.programName,
        firmChallengeRef: state.firmChallengeRef,
      });
      // Fire-and-forget — we don't want the dialog to block on a cold
      // bot. If this call itself 500s we still close; the operator can
      // click "Retry" on the failed row from the accounts page.
      void validateAccount(id).catch(() => {});
      handleClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSubmitError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [handleClose, isSubmitting, state]);

  const stepIndex = STEPS.indexOf(step);
  const meta = STEP_META[step];
  const HeaderIcon = meta.icon;

  const canAdvance = (() => {
    // Firm step only needs a firm — platform is picked inside the
    // Account step (pre-filled here when the firm is single-platform).
    if (step === "firm") return state.firm !== null;
    if (step === "program") {
      // Platform lives here now, so the step isn't done until it's
      // set along with challenge type and balance.
      return (
        state.platform !== null &&
        state.challengeType !== null &&
        state.accountSize !== null
      );
    }
    if (step === "credentials") {
      const baseValid = state.login.trim().length > 0 && state.password.length > 0;
      if (state.platform === "tradelocker") return baseValid && state.server.trim().length > 0;
      if (state.platform === "match-trader")
        return baseValid && state.accountLabel.trim().length > 0;
      return baseValid;
    }
    return false;
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : handleClose())}>
      <DialogContent
        showCloseButton={false}
        className="flex h-[90vh] max-h-[720px] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-[640px] lg:max-w-[760px]"
      >
        {/* ── Hero header ──────────────────────────────────────────── */}
        <div className="flex items-start gap-3 border-b border-border/40 px-4 pt-4 pb-4 sm:px-7 sm:pt-7 sm:pb-5">
          <motion.div
            key={step}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2, ease: EASE }}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent"
          >
            <HeaderIcon className="h-5 w-5" />
          </motion.div>
          <div className="min-w-0 flex-1 pr-10">
            <DialogTitle className="text-lg leading-tight font-medium">
              {meta.title}
            </DialogTitle>
            <DialogDescription className="mt-1 text-sm text-muted-foreground">
              {meta.description}
            </DialogDescription>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleClose}
            className="-mr-1 -mt-1 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* ── Step rail ───────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center gap-1.5 border-b border-border/40 px-3 py-3 sm:px-7 sm:py-3.5">
          {STEPS.map((s, i) => {
            const isDone = i < stepIndex;
            const isActive = i === stepIndex;
            return (
              <div key={s} className="flex flex-1 items-center gap-1.5">
                <StepChip
                  index={i + 1}
                  label={RAIL_LABELS[s]}
                  active={isActive}
                  done={isDone}
                />
                {i < STEPS.length - 1 && (
                  <ChevronRight
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 transition-colors",
                      isDone ? "text-accent/60" : "text-muted-foreground/40",
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* ── Content region ─────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-7 sm:py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22, ease: EASE }}
            >
              {step === "firm" && (
                <StepFirm
                  state={state}
                  marketType={marketType}
                  onSelect={(firm, platform) => {
                    // Platform comes back non-null only for single-
                    // platform firms. Multi-platform firms get a
                    // picker in the Account step; clear any stale
                    // pick from a previous firm so the UI asks fresh.
                    updateState({
                      firm,
                      platform,
                      programName: null,
                      accountSize: null,
                      steps: null,
                      challengeType: null,
                      firmChallengeRef: null,
                    });
                  }}
                />
              )}
              {step === "program" && (
                <StepProgram
                  state={state}
                  onPick={(pick) => updateState(pick)}
                  onPlatformChange={(platform) => {
                    // Swapping platforms mid-step invalidates the
                    // downstream picks — a different platform may
                    // surface a different firm_challenges catalog.
                    updateState({
                      platform,
                      programName: null,
                      accountSize: null,
                      steps: null,
                      challengeType: null,
                      firmChallengeRef: null,
                    });
                  }}
                />
              )}
              {step === "credentials" && (
                <StepCredentials state={state} onChange={updateState} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {submitError && (
          <div className="mx-4 mb-3 shrink-0 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive sm:mx-7">
            {submitError}
          </div>
        )}

        {/* ── Footer band ─────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border/40 bg-muted/20 px-3 py-3 sm:px-7 sm:py-3.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (step === "credentials") setStep("program");
              else if (step === "program") setStep("firm");
              else handleClose();
            }}
            disabled={isSubmitting}
            className="gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {step === "firm" ? "Cancel" : "Back"}
          </Button>
          <div className="flex items-center gap-3">
            <StepCounter index={stepIndex} total={STEPS.length} />
            {step === "credentials" ? (
              <Button
                size="sm"
                onClick={() => void submitCredentials()}
                disabled={!canAdvance || isSubmitting}
                className="gap-1.5"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    Add Account
                    <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => {
                  if (step === "firm") setStep("program");
                  else if (step === "program") setStep("credentials");
                }}
                disabled={!canAdvance}
                className="gap-1.5"
              >
                Next
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────

function StepChip({
  index,
  label,
  active,
  done,
}: {
  index: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
        active && "bg-accent/10 text-accent ring-1 ring-accent/30 ring-inset",
        done && "bg-accent/5 text-accent/80",
        !active && !done && "bg-muted/50 text-muted-foreground",
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold transition-colors",
          active && "bg-accent text-accent-foreground",
          done && "bg-accent/70 text-accent-foreground",
          !active && !done && "bg-muted-foreground/20 text-muted-foreground",
        )}
      >
        {done ? <Check className="h-3 w-3" strokeWidth={3} /> : index}
      </span>
      <span className="truncate">{label}</span>
    </div>
  );
}

function StepCounter({ index, total }: { index: number; total: number }) {
  if (index < 0 || index >= total) return null;
  return (
    <span className="hidden text-[11px] font-mono tabular-nums text-muted-foreground sm:inline">
      Step {index + 1} of {total}
    </span>
  );
}

function randomSuffix(n: number): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  let out = "";
  const buf = new Uint8Array(n);
  crypto.getRandomValues(buf);
  for (let i = 0; i < n; i++) out += alphabet[buf[i]! % alphabet.length];
  return out;
}
